<template>
  <div class="page-notifications">
    <div class="page-notifications__header">
      <NotificationsHeader
        class="page-notifications__heading"
        title-tag="h1"
        :unread-count="unreadCount"
        @mark-all="markAllAsRead"
      />
    </div>

    <div class="page-notifications__content">
      <BaseLoadingBar v-if="pending && events.length === 0" isSmall />
      <p v-else-if="events.length === 0" class="page-notifications__empty">
        <BaseIcon name="check" />
        {{ $t('notifications-center.empty') }}
      </p>
      <BasePhoneList
        v-else
        :items="groupedEvents"
        item-key="key"
        :has-more="canLoadMoreEvents"
        :loading-more="loadingMoreEvents"
        :load-more-error="loadMoreEventsError"
        :pending="pending"
        @load-more="loadMoreEvents"
      >
        <template #item="{ item }">
          <NotificationEventGroup
            :key="item.key"
            tag="li"
            class="page-notifications__group"
            :group="item"
            @open="open"
            @mark-read="markAsRead($event.id)"
          />
        </template>
      </BasePhoneList>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import NotificationEventGroup from '@/components/notifications-center/NotificationEventGroup.vue';
import NotificationsHeader from '@/components/notifications-center/NotificationsHeader.vue';
import notificationOpenMixin from '@/mixins/notification-open-mixin';
import { groupNotificationEvents } from '@/util/notification-changes-util';

// Where the list stood when a notification was opened from it. The page scrolls in the layout,
// not the window, so the router can't bring the reader back there.
let openedFrom = null;

// The notifications on a phone, where a panel under the bell would have no room: older ones load
// as the reader scrolls, and the back gesture returns here from the tender or opportunity opened.
export default {
  name: 'PageNotifications',
  components: {
    BaseIcon,
    BaseLoadingBar,
    BasePhoneList,
    NotificationEventGroup,
    NotificationsHeader,
  },
  mixins: [notificationOpenMixin],
  computed: {
    ...mapGetters('notifications', [
      'events',
      'unreadCount',
      'pending',
      'canLoadMoreEvents',
      'loadingMoreEvents',
      'loadMoreEventsError',
    ]),
    ...mapGetters('biDashboard', ['competitors']),
    groupedEvents() {
      return groupNotificationEvents(this.events, this.competitors);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.returnFrom(from));
  },
  created() {
    // What arrived since the last poll, and the competitors whose tenders gather in threads.
    this.getEvents();
    this.getAllCompetitors();
  },
  methods: {
    ...mapActions('notifications', ['getEvents', 'loadMoreEvents', 'markAllAsRead']),
    ...mapActions('biDashboard', ['getAllCompetitors']),
    scroller() {
      return document.querySelector('.base-layout__content');
    },
    open(notification) {
      const scroller = this.scroller();
      openedFrom = { subjectId: notification.content.subjectId, scrollTop: scroller ? scroller.scrollTop : 0 };
      this.openNotification(notification);
    },
    // Back from the notification's tender or opportunity: the list as the reader left it.
    returnFrom(from) {
      if (!openedFrom || !Object.values(from.params).includes(openedFrom.subjectId)) return;

      const { scrollTop } = openedFrom;
      openedFrom = null;
      this.$nextTick(() => {
        const scroller = this.scroller();
        if (scroller) scroller.scrollTop = scrollTop;
      });
    },
  },
};
</script>

<style lang="scss">
.page-notifications {
  &__header {
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;
    display: flex;
    align-items: center;
    height: $header-height;
    padding: 16px 32px;
    background: $color-white;
    box-shadow: $shadow-dropdown;

    @include phone-page-header {
      padding: 4px $phone-page-gutter;
    }
  }

  &__heading {
    flex: 1;
    min-width: 0;
  }

  // Raised above the header's own (scoped) rules.
  &__header .notifications-header .notifications-header__title {
    color: $color-normal;
    font-family: $font-family-raleway;
    font-size: $font-size-page-title;
    font-weight: normal;
    text-transform: uppercase;

    @include phone-page-title;
  }

  &__header .notifications-header .notifications-header__mark-all {
    flex-shrink: 0;
    min-height: 44px;
  }

  &__content {
    max-width: 720px;
    margin: 24px auto 0;
    padding: 0 32px;

    @include screen-below('tablet') {
      margin-top: $phone-page-gutter;
      padding: 0 $phone-page-gutter;
    }
  }

  &__group {
    padding: $space-sm;
    list-style: none;
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: $space-1;
    margin: 0;
    color: $color-text-muted;
    font-size: $font-size-small;

    .base-icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
    }
  }
}
</style>
